import { loadingState } from '../core/loadingState.js';
/**
 * portfolio-view.js — shared reseller "portfolio" building blocks.
 *
 * Portfolio mode = a reseller with a managed-client subtree and NO explicit
 * active org selected. The rule across the app: portfolio mode never shows
 * blended operational data. Operational tabs (magnets, A/B testing, ad
 * performance) render a per-client picker table instead; clicking a row
 * switches the whole app into that client's context (AuthController persists
 * the choice, /me refetches, and the scope-signature subscription in main.js
 * re-runs the current route under the new X-Active-Org). The exceptions are
 * Leads/Conversations (blended book views with client badges, see those
 * routes) and org-level tabs (Clients, Team, Settings), which are about the
 * reseller's own org and are not portfolio-gated.
 *
 * One table renderer serves every tab; each caller passes its own columns.
 */

import AuthController from './AuthController.js';
import { capList } from '../core/listExpand.js';
import { guardedFetch } from '../core/navGeneration.js';

export const inPortfolio = () =>
  AuthController.getManagedClients().length > 0 && !AuthController.getStoredActiveOrg();

export const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
export const fmtNum = (n) => Number(n || 0).toLocaleString();

const CHANNEL_CHIP = { reseller_org: 'Your org', managed_client: 'Client', saas_org: 'Org' };

let stylesInjected = false;
export function ensurePortfolioStyles() {
  if (stylesInjected) return;
  const s = document.createElement('style');
  s.id = 'm5t-portfolio-styles';
  s.textContent = `
    .page-shell .dash-clients { width:100%; border-collapse:collapse; font-size:0.86rem; }
    .page-shell .dash-clients th { text-align:right; font-size:0.7rem; font-weight:700; text-transform:uppercase; letter-spacing:0.05em; color:var(--color-text-subtle,#6C7C9A); padding:6px 10px; border-bottom:1px solid var(--color-surface-2,#EAF0F7); white-space:nowrap; }
    .page-shell .dash-clients th:first-child { text-align:left; }
    .page-shell .dash-clients td { padding:10px; text-align:right; border-bottom:1px solid var(--color-surface-2,#EAF0F7); font-variant-numeric:tabular-nums; white-space:nowrap; }
    .page-shell .dash-clients td:first-child { text-align:left; }
    .page-shell .dash-clients tr:last-child td { border-bottom:0; }
    .page-shell .dash-clients tbody tr { cursor:pointer; transition:background .12s ease; }
    .page-shell .dash-clients tbody tr:hover { background:var(--color-surface,#F7FAFD); }
    .page-shell .dash-clients .dc-name { font-weight:600; display:flex; align-items:center; gap:8px; }
    .page-shell .dash-clients .dc-chip { font-size:0.62rem; font-weight:700; letter-spacing:0.04em; padding:2px 7px; border-radius:999px; background:var(--color-surface-2,#EAF0F7); color:var(--color-text-muted,#4A5A78); }
    .page-shell .dash-clients .dc-delta-up { color:var(--color-tip,#059669); font-weight:600; }
    .page-shell .dash-clients .dc-delta-down { color:var(--color-danger,#DC2626); font-weight:600; }
    .page-shell .dash-clients .dc-view { color:var(--color-accent,#0072BA); font-weight:600; }
    .page-shell .dash-clients-scroll { overflow-x:auto; }
    .page-shell .pf-client-chip { font-size:0.64rem; font-weight:700; letter-spacing:0.03em; padding:2px 8px; border-radius:999px; background:var(--color-accent-soft,rgba(0,114,186,0.1)); color:var(--color-accent-hover,#00558D); white-space:nowrap; }
    .page-shell .pf-access-error { display:flex; align-items:center; gap:8px; font-size:0.84rem; font-weight:500; color:var(--color-danger,#DD4124); background:var(--color-danger-soft,rgba(221,65,36,0.08)); border:1px solid var(--color-danger,#DD4124); border-radius:var(--radius-md,10px); padding:10px 14px; margin-bottom:12px; }
    .page-shell .dc-skel { display:inline-block; width:44px; height:12px; border-radius:6px; background:var(--color-surface-2,#EAF0F7); animation:pf-skel 1.2s ease-in-out infinite; }
    @keyframes pf-skel { 0%,100% { opacity:1; } 50% { opacity:0.45; } }
  `;
  document.head.appendChild(s);
  stylesInjected = true;
}

const ymd = (d) => d.toISOString().slice(0, 10);

/** by_owner rollup rows for the caller's book (default: last 30 days). */
export async function fetchPortfolioRows(base, { days = 30 } = {}) {
  const now = new Date();
  const from = ymd(new Date(now.getTime() - (days - 1) * 86400000));
  const to = ymd(now);
  const res = await guardedFetch(`${base}/m5t/v5/dashboard/stats?from=${from}&to=${to}&group_by=owner`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const s = await res.json();
  if (!s.ok) throw new Error('stats not ok');
  return s.by_owner || [];
}

/**
 * One row per client org. `columns` = [{ h, td(row) → html }] for the metric
 * cells; the client-name cell and trailing "View" affordance are fixed.
 */
export function clientsTableHTML(rows, columns) {
  const list = rows;
  return `<div class="dash-clients-scroll"><table class="dash-clients">
    <thead><tr>
      <th>Client</th>${columns.map((c) => `<th>${c.h}</th>`).join('')}<th></th>
    </tr></thead>
    <tbody>${list.map((r) => `
      <tr data-org="${esc(r.owner_guid)}" tabindex="0" role="button"
          aria-label="Open ${esc(r.name || 'client')}">
        <td><span class="dc-name">${esc(r.name || r.owner_guid)}
          <span class="dc-chip">${esc(CHANNEL_CHIP[r.channel] || r.channel || '')}</span></span></td>
        ${columns.map((c) => `<td>${c.td(r)}</td>`).join('')}
        <td><span class="dc-view">View →</span></td>
      </tr>`).join('')}</tbody>
  </table></div>`;
}

/**
 * Show/replace an inline "couldn't enter that org" message in `container`.
 * Deliberately not routed through AppBroadcast.UI_TOAST: that pipe is still an
 * unimplemented console.log stub app-wide (every route's local toast() helper
 * ultimately publishes to it), so a message sent through it would not actually
 * be visible to anyone. This mirrors the plain DOM-text pattern the create/edit
 * magnet modal already uses for its own inline errors (magnets-route.js's
 * #mag-f-error) rather than depending on that unrelated, currently-broken system.
 */
function showAccessError(container) {
  let el = container.querySelector('.pf-access-error');
  if (!el) {
    el = document.createElement('div');
    el.className = 'pf-access-error';
    container.prepend(el);
  }
  el.textContent = "You don't have direct access to manage this org. Use the admin console to act on its behalf.";
}

/** Click/keyboard on a client row → enter that client's context. */
export function wireClientRows(container) {
  if (!container) return;
  // Capped here rather than in clientsTableHTML because every caller already calls this
  // and none of them would need changing — the dashboard portfolio plus the analytics,
  // ad-performance and A/B pickers all land on this one function.
  capList(container.querySelector('.dash-clients tbody') || container, {
    rowSelector: 'tr[data-org]',
    noun: 'clients',
    controlHost: container.querySelector('.dash-clients-scroll') || container,
  });
  const open = async (tr) => {
    const guid = tr?.dataset?.org;
    if (!guid) return;
    // Not every "View" resolves: an org this caller can only see because they're a
    // platform admin (dashboard stats bypass owner-scoping for admins) but isn't a
    // direct member of or a reseller-subtree relation to sets activeOrgError — the
    // switch silently falls back to their default scope server-side. Surface that
    // honestly instead of leaving the click looking like it did nothing.
    const result = await AuthController.setActiveOrg(guid);
    if (result?.activeOrgError) showAccessError(container);
  };
  container.querySelectorAll('tbody tr[data-org]').forEach((tr) => {
    tr.addEventListener('click', () => open(tr));
    tr.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(tr); }
    });
  });
}

const emptyState = (icon, msg) =>
  `<div class="m5t-empty"><span class="material-symbols-outlined" aria-hidden="true">${icon}</span><p>${msg}</p></div>`;

/**
 * Page shell for a portfolio-gated tab: same header chrome as the tab itself,
 * one Clients card whose body the picker fills in.
 */
export function pickerPageHTML({ section, title, description }) {
  ensurePortfolioStyles();
  return `
    <div class="page-shell" data-section="${esc(section)}">
      <div class="page-header">
        <div class="page-header-text">
          <h1 class="page-title">${esc(title)}</h1>
          <p class="page-description">${esc(description)}</p>
        </div>
      </div>
      <div class="dashboard-card">
        <div class="card-header">
          <h2 class="card-title"><span class="material-symbols-outlined" aria-hidden="true">groups</span> Clients</h2>
        </div>
        <div class="card-body" id="pf-picker-body">${loadingState('list', 'Loading clients')}</div>
      </div>
    </div>`;
}

/**
 * The picker table at its FINAL height, before any fetch: the client list
 * (names, guids, row count) is already known synchronously from the cached
 * scope - only the metrics need the network. Painting the real rows with
 * shimmer cells reserves the exact space, so the stats fill a table that
 * already owns its height instead of pushing the page down (the
 * space-reservation rule - late content fills, never shifts). Rows are also
 * clickable immediately: entering a client's context needs no stats.
 * `columns` only contributes headers here; metric cells are placeholders.
 */
export function pickerSkeletonHTML(columns) {
  const known = AuthController.getManagedClients()
    .map((c) => ({ owner_guid: c.orgGuid, name: c.name, channel: 'managed_client' }));
  if (!known.length) return loadingState('list', 'Loading clients');
  return clientsTableHTML(known, columns.map((c) => ({
    h: c.h, td: () => '<span class="dc-skel" aria-hidden="true"></span>',
  })));
}

/** Fetch + render + wire the picker into #pf-picker-body. */
export async function initPicker(base, columns, { emptyMsg } = {}) {
  const el = document.getElementById('pf-picker-body');
  if (!el) return;
  el.innerHTML = pickerSkeletonHTML(columns);
  wireClientRows(el);
  try {
    const rows = await fetchPortfolioRows(base);
    if (!rows.length) {
      el.innerHTML = emptyState('groups', emptyMsg || 'No client orgs yet. Create one under Clients first.');
      return;
    }
    el.innerHTML = clientsTableHTML(rows, columns);
    wireClientRows(el);
  } catch (err) {
    console.warn('[portfolio] picker load failed:', err);
    el.innerHTML = emptyState('error', 'Could not load your clients. Please refresh.');
  }
}

/** owner_guid → display name for the caller's book (for blended-view badges). */
export function clientNameMap() {
  const map = {};
  AuthController.getManagedClients().forEach((c) => { map[c.orgGuid] = c.name; });
  AuthController.getMemberships().forEach((m) => { if (m.orgGuid && !map[m.orgGuid]) map[m.orgGuid] = m.name || ''; });
  return map;
}

/** Badge shown on blended rows in portfolio mode. */
export const clientChipHTML = (name) =>
  name ? `<span class="pf-client-chip">${esc(name)}</span>` : '';

export default {
  inPortfolio, ensurePortfolioStyles, fetchPortfolioRows, clientsTableHTML,
  wireClientRows, pickerPageHTML, pickerSkeletonHTML, initPicker, clientNameMap, clientChipHTML,
};
